import {
  AfterViewInit,
  Directive,
  EventEmitter,
  Inject,
  Input,
  Output,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import {
  concatMap,
  from,
  fromEvent,
  mergeMap,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  takeWhile,
  timer,
} from 'rxjs';
import { debounceTime, delay, filter, map, tap } from 'rxjs/operators';
import { AutoUnsubscribe } from '../../../common/decorators';
import { DOCUMENT } from '@angular/common';
import { CroppedValueStateModel, ExpandableContentState } from './info-field-expandable-value-list.model';

@Directive({
  selector: '[cropContentUntilFit]',
})
@AutoUnsubscribe
export class InfoFieldExpandableValueListDirective implements AfterViewInit {
  @Input()
  set cropContentUntilFit(items: string[]) {
    this.items = items;
  }
  @Input() set opened(isOpened: boolean | null) {
    if (isOpened !== null) {
      this.isOpened$.next(isOpened);
      this.isOpened = isOpened;
    }
  }

  @Output() fitEvent: EventEmitter<boolean> = new EventEmitter<boolean>(true);

  private items: string[] = [];
  private fullValue = '';
  private croppedValue = '';
  private isOpened$: Subject<boolean> = new Subject<boolean>();
  private isOpened = false;
  private iconWidth = 20;
  private subscriptions: Subscription[] = [];

  constructor(
    private templateRef: TemplateRef<unknown>,
    private vcRef: ViewContainerRef,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngAfterViewInit(): void {
    this.fullValue = this.getFullValue(this.items);
    const fits = this.checkIfFits(this.fullValue);

    if (!fits) {
      this.subscriptions.push(
        this.getCroppedValueThatFits$().subscribe(({ croppedValue }: CroppedValueStateModel) => {
          this.croppedValue = croppedValue;
          this.updateView(this.croppedValue);
        }),
      );
    } else {
      this.updateView(this.fullValue);
    }

    this.fitEvent.next(fits);

    const statesMap = {
      [ExpandableContentState.Opened]: {
        first: () => this.updateView(this.fullValue),
        second: () => of(null).pipe(tap(() => this.setOpenState(true))),
        delay: 0,
      },
      [ExpandableContentState.Closed]: {
        first: () => this.setOpenState(false),
        second: () =>
          this.getCroppedValueThatFits$().pipe(
            tap(({ croppedValue }: CroppedValueStateModel) => {
              this.croppedValue = croppedValue;
              this.updateView(this.croppedValue);
            }),
          ),
        delay: 200,
      },
    };

    this.subscriptions.push(
      fromEvent(this.document.defaultView, 'resize')
        .pipe(
          filter(() => !this.isOpened),
          debounceTime(300),
          switchMap(() => this.getCroppedValueThatFits$()),
        )
        .subscribe(({ croppedValue, fitsOnIteration }: CroppedValueStateModel) => {
          this.croppedValue = croppedValue;
          this.fitEvent.next(fitsOnIteration);
          this.updateView(fitsOnIteration ? this.fullValue : this.croppedValue);
        }),
      this.isOpened$
        .pipe(
          switchMap((isOpened: boolean) => {
            const state = statesMap[isOpened ? ExpandableContentState.Opened : ExpandableContentState.Closed];

            return of(null).pipe(
              map(() => state.first()),
              switchMap(() => timer(state.delay).pipe(switchMap(() => state.second()))),
            );
          }),
        )
        .subscribe(),
    );
  }

  private getCroppedValueThatFits$(): Observable<CroppedValueStateModel> {
    let stopIteration = false;
    let iterationIndex = 0;

    return from(this.items).pipe(
      mergeMap((item: string) => of(item)),
      concatMap((item: string) => of(item).pipe(delay(0))),
      takeWhile(() => stopIteration !== true && iterationIndex < this.items.length - 1),
      map(() => {
        iterationIndex++;

        const croppedValue = this.getCroppedValue([
          ...this.items.slice(0, iterationIndex),
          this.items[this.items.length - 1],
        ]);
        const fitsOnIteration = this.checkIfFits(croppedValue);

        if (iterationIndex === 1 && !fitsOnIteration) {
          stopIteration = !fitsOnIteration;
          return { croppedValue: `${this.items[0]}, ...`, fitsOnIteration };
        }

        stopIteration = !fitsOnIteration;

        return { croppedValue: fitsOnIteration ? croppedValue : this.croppedValue, fitsOnIteration };
      }),
    );
  }

  private checkIfFits(value: string): boolean {
    this.vcRef.clear();
    this.vcRef.createEmbeddedView(this.templateRef, { value }).detectChanges();
    const valueElement = this.getValueElement();

    if (valueElement) {
      this.renderer.setStyle(valueElement, 'visibility', 'hidden');
      this.renderer.setStyle(valueElement, 'white-space', 'nowrap');
    }

    return this.getValueFitStatus();
  }

  private getValueFitStatus(): boolean {
    const valueElement = this.getValueElement();
    return valueElement.scrollWidth <= valueElement.parentElement.clientWidth - this.iconWidth;
  }

  private getFullValue(items: string[]): string {
    if (items?.length) {
      return items.reduce((acc: string, item: string) => (acc = acc === '' ? `${item}` : `${acc}, ${item}`), '');
    }

    return '';
  }

  private getCroppedValue(items: string[]): string {
    if (items?.length > 1) {
      return `${items.slice(0, items.length - 1).join(', ')}, ..., ${items[items.length - 1]}`;
    }

    return this.getFullValue(items);
  }

  private getValueElement(): HTMLElement {
    return this.templateRef.elementRef?.nativeElement?.parentElement?.querySelector('.content-value');
  }

  private updateView(value: string): void {
    this.vcRef.clear();
    this.vcRef
      .createEmbeddedView(this.templateRef, {
        value,
      })
      .detectChanges();
  }

  private setOpenState(shouldOpen: boolean): void {
    const valueElement = this.getValueElement();

    if (valueElement) {
      shouldOpen
        ? this.renderer.addClass(valueElement.parentElement, 'info-field-list__content_open')
        : this.renderer.removeClass(valueElement.parentElement, 'info-field-list__content_open');
    }
  }
}
